import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, Alert, Spinner, Row, Col } from "reactstrap";
import { post } from "helpers/api_helper";

const RequestEditModal = ({ isOpen, toggle, documentType, documentId, documentNumber, onSuccess }) => {
    const [docType, setDocType] = useState(documentType || "order");
    const [docNumber, setDocNumber] = useState(documentNumber || documentId || "");
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    const isPrefilled = Boolean(documentId || documentNumber);

    useEffect(() => {
        if (isOpen) {
            setDocType(documentType || "order");
            setDocNumber(documentNumber || documentId || "");
            setReason("");
            setError(null);
            setSuccessMsg(null);
        }
    }, [isOpen, documentType, documentId, documentNumber]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const finalDocType = isPrefilled ? (documentType || "order") : docType;
        const finalDocNum = (isPrefilled ? (documentNumber || documentId) : docNumber)?.toString().trim();

        if (!finalDocNum) {
            setError(`Please enter the ${finalDocType === "order" ? "Order Number" : "Invoice Number"}.`);
            return;
        }

        if (!reason.trim()) {
            setError("Please enter a reason for requesting an edit.");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccessMsg(null);

        try {
            const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
            const payload = {
                document_type: finalDocType,
                document_id: isPrefilled ? documentId || finalDocNum : finalDocNum,
                document_number: finalDocNum,
                reason: reason.trim(),
                requested_by_name: authUser.username || authUser.name || "Data Entry User"
            };

            const res = await post("/api/approvals/request", payload);
            if (res.status === 200) {
                setSuccessMsg(res.message || "Edit request submitted successfully. Waiting for Admin approval.");
                setReason("");
                if (onSuccess) {
                    setTimeout(() => {
                        onSuccess();
                    }, 1200);
                }
            } else {
                setError(res.message || "Failed to submit edit request.");
            }
        } catch (err) {
            setError(err?.message || "An unexpected error occurred while submitting the request.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered zIndex="99999">
            <ModalHeader toggle={toggle}>
                <i className="mdi mdi-lock-alert text-warning me-2"></i>
                Request Edit Approval {isPrefilled ? `: ${(documentType || "order").toUpperCase()} #${documentNumber || documentId}` : ""}
            </ModalHeader>
            <form onSubmit={handleSubmit}>
                <ModalBody>
                    <div className="alert alert-info py-2 mb-3" style={{ fontSize: "13px" }}>
                        <i className="mdi mdi-information me-1"></i>
                        Direct editing is restricted. Submitting this request will notify an <strong>Administrator</strong> for one-time edit authorization.
                    </div>

                    {error && <Alert color="danger" className="py-2">{error}</Alert>}
                    {successMsg && <Alert color="success" className="py-2">{successMsg}</Alert>}

                    {!isPrefilled && (
                        <Row className="mb-3">
                            <Col sm={5}>
                                <FormGroup className="mb-0">
                                    <Label for="reqDocType" className="fw-semibold font-size-13">
                                        Type <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="select"
                                        id="reqDocType"
                                        value={docType}
                                        onChange={(e) => setDocType(e.target.value)}
                                        disabled={loading || Boolean(successMsg)}
                                    >
                                        <option value="order">Order</option>
                                        <option value="invoice">Invoice</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col sm={7}>
                                <FormGroup className="mb-0">
                                    <Label for="reqDocNumber" className="fw-semibold font-size-13">
                                        {docType === "order" ? "Order #" : "Invoice #"} <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="text"
                                        id="reqDocNumber"
                                        placeholder={docType === "order" ? "e.g. 1042 or ORD-2026-..." : "e.g. INV-1002"}
                                        value={docNumber}
                                        onChange={(e) => {
                                            setDocNumber(e.target.value);
                                            if (error) setError(null);
                                        }}
                                        disabled={loading || Boolean(successMsg)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}

                    <FormGroup className="mb-3">
                        <Label for="editReason" className="fw-semibold font-size-13">
                            Reason for Editing <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="textarea"
                            id="editReason"
                            rows="3"
                            placeholder="Explain why changes are needed (e.g., corrected patient tooth shade, adjusted quantity, doctor requested modification)..."
                            value={reason}
                            onChange={(e) => {
                                setReason(e.target.value);
                                if (error) setError(null);
                            }}
                            disabled={loading || Boolean(successMsg)}
                            required
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={loading || Boolean(successMsg)}>
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" /> Submitting...
                            </>
                        ) : (
                            <>
                                <i className="mdi mdi-send me-1"></i> Send Request
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </form>
        </Modal>
    );
};

export default RequestEditModal;
